import { Pipe, PipeTransform } from '@angular/core';

// Tags allowed to survive sanitization. Matches the notification Quill toolbar
// (bold + link) plus the block/inline wrappers Quill emits around that content.
const ALLOWED_TAGS = new Set(['P', 'BR', 'SPAN', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'A']);

// Tags dropped together with their content (never surfaced, even as text).
const DROP_TAGS = new Set([
  'SCRIPT',
  'STYLE',
  'IFRAME',
  'OBJECT',
  'EMBED',
  'LINK',
  'META',
  'BASE',
  'FORM',
  'INPUT',
  'TEXTAREA',
  'BUTTON',
  'SELECT'
]);

const ALLOWED_HREF_PROTOCOLS = ['http:', 'https:', 'mailto:'];

/**
 * Strips disallowed markup from admin-authored notification rich-text before it is
 * rendered through `[innerHTML]`, so tags such as `<h1>`/`<label>` cannot be injected
 * (RITM2523700, finding #02). Only the allowlist above survives; disallowed elements are
 * unwrapped to their text content, and `<a>` keeps only a protocol-safe `href`.
 *
 * Returns a plain `string` (not `SafeHtml`) on purpose: the binding stays a normal
 * `[innerHTML]`, so Angular's built-in sanitizer still runs as a second layer. Never route
 * this through `DomSanitizer.bypassSecurityTrustHtml` — that would disable that layer.
 */
@Pipe({
  name: 'sanitizeRichText',
  standalone: true
})
export class SanitizeRichTextPipe implements PipeTransform {
  public transform(value: string | null | undefined): string {
    if (!value) {
      return '';
    }

    const doc = new DOMParser().parseFromString(value, 'text/html');
    this.sanitizeChildren(doc.body);

    return doc.body.innerHTML;
  }

  private sanitizeChildren(node: Node): void {
    // Snapshot children first: the loop mutates the tree (unwrap/remove).
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        this.sanitizeElement(child as Element);
      } else if (child.nodeType !== Node.TEXT_NODE) {
        // Drop comments, processing instructions, etc.
        child.parentNode?.removeChild(child);
      }
    }
  }

  private sanitizeElement(element: Element): void {
    if (DROP_TAGS.has(element.tagName)) {
      element.remove();

      return;
    }

    // Clean descendants before deciding what to do with this element.
    this.sanitizeChildren(element);

    if (ALLOWED_TAGS.has(element.tagName)) {
      this.sanitizeAttributes(element);
    } else {
      this.unwrap(element);
    }
  }

  private sanitizeAttributes(element: Element): void {
    const isAnchor = element.tagName === 'A';
    const hasSafeHref = isAnchor && this.isSafeHref(element.getAttribute('href'));

    for (const attr of Array.from(element.attributes)) {
      const keepHref = isAnchor && attr.name === 'href' && hasSafeHref;
      if (!keepHref) {
        element.removeAttribute(attr.name);
      }
    }

    if (isAnchor && hasSafeHref) {
      element.setAttribute('target', '_blank');
      element.setAttribute('rel', 'noopener noreferrer');
    }
  }

  private isSafeHref(href: string | null): boolean {
    if (!href) {
      return false;
    }

    try {
      const url = new URL(href, document.baseURI);

      return ALLOWED_HREF_PROTOCOLS.includes(url.protocol);
    } catch {
      return false;
    }
  }

  private unwrap(element: Element): void {
    const parent = element.parentNode;
    if (!parent) {
      return;
    }

    while (element.firstChild) {
      parent.insertBefore(element.firstChild, element);
    }
    parent.removeChild(element);
  }
}
